import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { TFunction } from 'i18next';

interface DateWithLetterMonthProps {
  dateString: string;
  monthAbbreviated?: boolean;
}

dayjs.extend(duration);

export const getFormattedDate = (isoString: string, useSlash = false) => {
  const date = new Date(isoString);

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  return useSlash ? `${day}/${month}/${year}` : `${day}-${month}-${year}`;
};

export const getFormattedTime = (isoString: string) => {
  const date = new Date(isoString);

  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${hours}:${minutes}`;
};

export const formatDateWithLetterMonth = ({
  dateString,
  monthAbbreviated = true,
}: DateWithLetterMonthProps) => {
  const date = new Date(dateString);

  // Get day without leading zeros
  const day = date.getDate();

  // Define month abbreviations
  const monthAbbr = [
    'Jan',
    'Fev',
    'Mar',
    'Avr',
    'Mai',
    'Juin',
    'Juil',
    'Août',
    'Sep',
    'Oct',
    'Nov',
    'Déc',
  ];

  // Define month WITHOUT abbreviations
  const fullLetterMonth = [
    'Janvier',
    'Février',
    'Mars',
    'Avril',
    'Mai',
    'Juin',
    'Juillet',
    'Août',
    'Septembre',
    'Octobre',
    'Novembre',
    'Décembre',
  ];

  const month = monthAbbreviated
    ? monthAbbr[date.getMonth()] + '.'
    : fullLetterMonth[date.getMonth()];

  // Get the full year
  const year = date.getFullYear();

  return `${day} ${month} ${year}`;
};

export const getMonthsUpToCurrent = (t: TFunction<undefined>) => {
  const months = [
    `${t('common:january_short')}.`,
    `${t('common:february_short')}.`,
    `${t('common:march_short')}.`,
    `${t('common:april_short')}.`,
    `${t('common:may_short')}.`,
    `${t('common:june_short')}.`,
    `${t('common:july_short')}.`,
    `${t('common:august_short')}.`,
    `${t('common:september_short')}.`,
    `${t('common:october_short')}.`,
    `${t('common:november_short')}.`,
    `${t('common:december_short')}.`,
  ];
  const currentMonth = new Date().getMonth(); // 0-based index (0 = January)
  return months?.slice(0, currentMonth + 1);
};

export const formatMillisecondsToMinutesSeconds = (milliseconds: number) => {
  const durationObj = dayjs.duration(milliseconds);
  const minutes = durationObj.minutes().toString().padStart(2, '0');
  const seconds = durationObj.seconds().toString().padStart(2, '0');
  return `${minutes}:${seconds}`;
};

export const getDifferenceInMilliseconds = (
  start: number,
  end: number,
): string => {
  const difference = end - start;
  return formatMillisecondsToMinutesSeconds(difference);
};

export const getDateInISOFormat = (date: Date) => {
  return date.toISOString()?.slice(0, -1);
};

export const getDifferenceInHoursMinutesSeconds = (
  startDate: string,
  endDate: string,
): string => {
  const start = dayjs(startDate);
  const end = dayjs(endDate);

  const differenceInMilliseconds = end.diff(start);

  const minutes = Math.floor(
    (differenceInMilliseconds % (1000 * 60 * 60)) / (1000 * 60),
  )
    .toString()
    .padStart(2, '0');
  const seconds = Math.floor((differenceInMilliseconds % (1000 * 60)) / 1000)
    .toString()
    .padStart(2, '0');

  return `${minutes}:${seconds}`;
};

export const formatSecondsToMinutesSeconds = (seconds: number): string => {
  const durationObj = dayjs.duration(seconds * 1000); // Convert seconds to milliseconds
  const minutes = durationObj.minutes().toString().padStart(2, '0');
  const remainingSeconds = durationObj.seconds().toString().padStart(2, '0');
  return `${minutes}:${remainingSeconds}`;
};

export const setDateToLastYear = (date: string) => {
  // date format: 2026-09-15T16:02:13.018
  const year = Number(date.slice(0, 4));
  const updatedAtWithoutYear = date.slice(4);

  return `${year - 1}${updatedAtWithoutYear}`;
};
